import { useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCustomerServices } from "app/hooks/useCustomerServices";
import { useDashboardSlice } from "app/pages/Customer/MyAccount/Dashboard/slice";
import { useFixedPhoneDashboard } from "../../../../pages/Customer/FixedPhone/Account/Dashboard/hooks/useFixedPhoneDashboard";
import { useFixedPhoneSlice } from "app/pages/Customer/FixedPhone/slice";
import { useServiceTab } from "app/components/AdminLayout/MyAccountLayout/service-tab-context";

export type AccountStatusViewModel = {
  isActive: boolean | null;
  isLoading: boolean;
  getAccountStatus: () => void;
};

export const useAccountStatus = (): AccountStatusViewModel => {
  const { customerId, hasFixedPhoneService, hasMobileService } =
    useCustomerServices();
  const location = useLocation();
  const { dashboardActiveTab, simServiceActiveTab } = useServiceTab();
  const {
    useLazyGetDashboardDataQuery,
    useLazyGetDigitalSecurityDashboardQuery,
  } = useDashboardSlice();
  const [
    getDashboardData,
    {
      data: mobileDashboardData,
      isLoading: isMobileDashboardLoading,
      // isFetching: isMobileDashboardFetching,
    },
  ] = useLazyGetDashboardDataQuery();

  const [
    getDigitalSecurityDashboard,
    { data: digitalSecurityDashboardData, isLoading: isDigitalSecurityLoading },
  ] = useLazyGetDigitalSecurityDashboardQuery();

  const {
    dashboardData: fixedPhoneDashboardData,
    isLoading: isFixedPhoneDashboardLoading,
    getFixedPhoneDashboard,
  } = useFixedPhoneDashboard(customerId);

  // Only fetch account details if we are on the Sim Service page AND the Fixed Phone tab is active
  const isOnSimService = location.pathname.includes("/my-account/sim-service");
  const shouldFetchAccountDetails =
    isOnSimService && simServiceActiveTab === "fixedPhone";

  const { useGetFixedPhoneAccountDetailsQuery } = useFixedPhoneSlice();
  const {
    data: fixedPhoneAccountDetailsData,
    isLoading: isFixedPhoneAccountDetailsLoading,
    // isFetching: isFixedPhoneAccountDetailsFetching,
  } = useGetFixedPhoneAccountDetailsQuery(void 0, {
    skip: !customerId || !hasFixedPhoneService || !shouldFetchAccountDetails,
  });

  const accountStatus = useMemo(() => {
    const isOnDashboard = location.pathname.includes("/my-account/dashboard");

    // If on dashboard, return status based on the currently active tab
    if (isOnDashboard) {
      if (dashboardActiveTab === "fixedPhone") {
        // Return fixed phone account status when fixed phone tab is active
        if (fixedPhoneDashboardData?.accountStatus) {
          const status = fixedPhoneDashboardData.accountStatus.status;
          const isActive = fixedPhoneDashboardData.accountStatus.isActive;
          return status?.toLowerCase() === "active" || isActive;
        }
        return null;
      }

      if (dashboardActiveTab === "digitalSecurity") {
        if (digitalSecurityDashboardData?.data?.status) {
          const status = digitalSecurityDashboardData.data.status;
          return status === "active";
        }
        return null;
      }

      if (dashboardActiveTab === "mobile") {
        // Return mobile dashboard status when mobile tab is active
        if (mobileDashboardData?.data?.status) {
          const status = mobileDashboardData.data.status;
          return status === "active" || status !== "accountSuspended";
        }
        // Fallback to digital security status if mobile data is missing
        if (digitalSecurityDashboardData?.data?.status) {
          const status = digitalSecurityDashboardData.data.status;
          return status === "active";
        }
        return null;
      }
    }

    // If on sim-service, return status based on the currently active tab
    if (isOnSimService) {
      if (simServiceActiveTab === "fixedPhone") {
        // Return fixed phone account status from account details when fixed phone tab is active
        if (fixedPhoneAccountDetailsData?.data?.account?.accountStatus) {
          const status =
            fixedPhoneAccountDetailsData.data.account.accountStatus;
          return status?.toLowerCase() === "active";
        }
        // Fallback to dashboard data if details are missing
        if (fixedPhoneDashboardData?.accountStatus) {
          const status = fixedPhoneDashboardData.accountStatus.status;
          const isActive = fixedPhoneDashboardData.accountStatus.isActive;
          return status?.toLowerCase() === "active" || isActive;
        }
        return null;
      }

      if (simServiceActiveTab === "mobile") {
        // Return mobile dashboard status when mobile tab is active
        if (mobileDashboardData?.data?.status) {
          const status = mobileDashboardData.data.status;
          return status === "active" || status !== "accountSuspended";
        }
        // Fallback to digital security status if mobile data is missing
        if (digitalSecurityDashboardData?.data?.status) {
          const status = digitalSecurityDashboardData.data.status;
          return status === "active";
        }
        return null;
      }
    }

    // For all other My Account pages (payment history, manage profile, etc.)
    // Show status based on available data - prefer Fixed Phone Dashboard, then Fixed Phone Account Details, then Mobile
    if (fixedPhoneDashboardData?.accountStatus) {
      const status = fixedPhoneDashboardData.accountStatus.status;
      const isActive = fixedPhoneDashboardData.accountStatus.isActive;
      return status?.toLowerCase() === "active" || isActive;
    }

    if (fixedPhoneAccountDetailsData?.data?.account?.accountStatus) {
      const status = fixedPhoneAccountDetailsData.data.account.accountStatus;
      return status?.toLowerCase() === "active";
    }

    if (mobileDashboardData?.data?.status) {
      const status = mobileDashboardData.data.status;
      return status === "active" || status !== "accountSuspended";
    }

    if (digitalSecurityDashboardData?.data?.status) {
      const status = digitalSecurityDashboardData.data.status;
      return status === "active";
    }

    return null;
  }, [
    location.pathname,
    dashboardActiveTab,
    simServiceActiveTab,
    fixedPhoneDashboardData,
    fixedPhoneAccountDetailsData,
    mobileDashboardData,
    digitalSecurityDashboardData,
    isOnSimService,
  ]);

  const isLoading = useMemo(() => {
    const isOnDashboard = location.pathname.includes("/my-account/dashboard");

    if (isOnDashboard) {
      if (dashboardActiveTab === "fixedPhone") {
        return isFixedPhoneDashboardLoading;
      }
      if (dashboardActiveTab === "digitalSecurity") {
        return isDigitalSecurityLoading;
      }
      if (dashboardActiveTab === "mobile") {
        return isMobileDashboardLoading;
      }
      // If no active tab, check if any data is loading
      return (
        isFixedPhoneDashboardLoading ||
        isMobileDashboardLoading ||
        isDigitalSecurityLoading
      );
    }

    if (isOnSimService) {
      if (simServiceActiveTab === "fixedPhone") {
        // Only include details loading if we interpret we should be fetching it
        return (
          isFixedPhoneAccountDetailsLoading || isFixedPhoneDashboardLoading
        );
      }
      if (simServiceActiveTab === "mobile") {
        return isMobileDashboardLoading || isDigitalSecurityLoading;
      }
      // If no active tab, check if any data is loading
      return (
        isFixedPhoneAccountDetailsLoading ||
        isFixedPhoneDashboardLoading ||
        isMobileDashboardLoading ||
        isDigitalSecurityLoading
      );
    }

    // For other pages, check if any relevant data is loading
    return (
      isFixedPhoneDashboardLoading ||
      isMobileDashboardLoading ||
      isDigitalSecurityLoading
    );
  }, [
    location.pathname,
    dashboardActiveTab,
    simServiceActiveTab,
    isFixedPhoneDashboardLoading,
    isFixedPhoneAccountDetailsLoading,
    isMobileDashboardLoading,
    isDigitalSecurityLoading,
    isOnSimService,
  ]);

  const getAccountStatus = useCallback(() => {
    if (customerId) {
      if (hasMobileService) {
        getDashboardData({ customerId });
        getDigitalSecurityDashboard({ customerId });
      }
      if (hasFixedPhoneService) {
        getFixedPhoneDashboard({ customerId });
      }
    }
  }, [
    customerId,
    hasFixedPhoneService,
    hasMobileService,
    getDashboardData,
    getDigitalSecurityDashboard,
    getFixedPhoneDashboard,
  ]);

  return {
    isActive: accountStatus,
    isLoading,
    getAccountStatus,
  };
};
